/**
 * Server PDF List Component
 * Displays list of drawing PDFs (uploaded by any user) from server for selection.
 * Mirrors ServerCADList (csv feature) — selection is always manual, upload is
 * always offered alongside the list (whether it has files or not).
 */

import { useState, useEffect } from 'react'
import { FileStorageService } from '../../../services/FileStorageService'
import { FileInfo } from '../../../models/FileStorage'
import styles from '../../csv/ui/ServerFileList.module.css'

interface ServerPDFListProps {
  projectId: number
  onFileSelect: (file: FileInfo) => void
  onCancel: () => void
  onUploadClick: () => void
}

const PAGE_SIZE = 5

export function ServerPDFList({ projectId, onFileSelect, onCancel, onUploadClick }: ServerPDFListProps) {
  const [files, setFiles] = useState<FileInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<FileInfo | null>(null)
  const [page, setPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    loadFiles(page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, page])

  const loadFiles = async (targetPage: number) => {
    try {
      setLoading(true)
      setError(null)

      const response = await FileStorageService.listFiles(projectId, 'pdf', targetPage, PAGE_SIZE)

      setFiles(response.data?.files || [])
      setTotalCount(response.data?.total_count || 0)
      setTotalPages(response.data?.pagination?.total_pages || 0)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '도면 PDF 목록을 불러오지 못했습니다.'
      console.error('[ServerPDFList] Error loading PDF files:', errorMessage)
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handlePrevPage = () => {
    if (page > 1) setPage(page - 1)
  }

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1)
  }

  const handleFileClick = (file: FileInfo) => {
    setSelectedFile(file)
  }

  const handleSelectClick = () => {
    if (selectedFile) {
      onFileSelect(selectedFile)
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    })
  }

  if (loading) {
    return (
      <div className={styles.overlay}>
        <div className={styles.container}>
          <div className={styles.header}>
            <h2 className={styles.title}>서버 도면 PDF</h2>
            <p className={styles.description}>파일 목록 불러오는 중...</p>
          </div>
          <div className={styles.spinner} />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.overlay} onClick={onCancel}>
        <div className={styles.container} onClick={(e) => e.stopPropagation()}>
          <div className={styles.header}>
            <h2 className={styles.title}>서버 도면 PDF</h2>
            <p className={styles.error}>{error}</p>
          </div>
          <div className={styles.actions}>
            <button className={styles.cancelButton} onClick={onCancel}>
              뒤로
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (files.length === 0) {
    return (
      <div className={styles.overlay} onClick={onCancel}>
        <div className={styles.container} onClick={(e) => e.stopPropagation()}>
          <div className={styles.header}>
            <h2 className={styles.title}>서버 도면 PDF</h2>
          </div>
          <div className={styles.emptyState}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <p className={styles.emptyTitle}>도면 PDF가 없습니다</p>
            <p className={styles.emptyDescription}>도면 PDF를 업로드해주세요</p>
          </div>
          <div className={styles.actions}>
            <button className={styles.cancelButton} onClick={onCancel}>
              뒤로
            </button>
            <button className={styles.selectButton} onClick={onUploadClick}>
              PDF 업로드
            </button>
          </div>
        </div>
      </div>
    )
  }

  const isSelected = (file: FileInfo) => selectedFile?.filename === file.filename

  return (
    <div className={styles.overlay} onClick={onCancel}>
      <div className={styles.container} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>서버 도면 PDF</h2>
          <p className={styles.description}>CCTV·구역 매핑에 쓸 도면 PDF를 선택하세요</p>
        </div>

        <div className={styles.fileList}>
          {files.map((file) => (
            <button
              key={file.filename}
              className={`${styles.fileItem} ${isSelected(file) ? styles.selected : ''}`}
              onClick={() => handleFileClick(file)}
            >
              <svg className={styles.fileIcon} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <path d="M9 15h6" />
                <path d="M12 18v-6" />
              </svg>
              <div className={styles.fileInfo}>
                <p className={styles.fileName}>{file.original_name}</p>
                <div className={styles.fileMeta}>
                  <span className={styles.fileSize}>{formatFileSize(file.size_bytes)}</span>
                  <span className={styles.fileDate}>{formatDate(file.upload_date)}</span>
                </div>
              </div>
              {isSelected(file) && (
                <svg className={styles.checkmark} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              className={styles.pageButton}
              onClick={handlePrevPage}
              disabled={page <= 1}
              aria-label="이전 페이지"
            >
              ‹
            </button>
            <span className={styles.pageInfo}>
              {page} / {totalPages}
              <span className={styles.totalCount}> · 총 {totalCount}개</span>
            </span>
            <button
              className={styles.pageButton}
              onClick={handleNextPage}
              disabled={page >= totalPages}
              aria-label="다음 페이지"
            >
              ›
            </button>
          </div>
        )}

        <div className={styles.actions}>
          <button className={styles.cancelButton} onClick={onCancel}>
            뒤로
          </button>
          <button className={styles.cancelButton} onClick={onUploadClick}>
            새 파일 업로드
          </button>
          <button
            className={styles.selectButton}
            onClick={handleSelectClick}
            disabled={!selectedFile}
          >
            파일 선택
          </button>
        </div>
      </div>
    </div>
  )
}
